import { useMemo } from "react";
import { navGroups } from "./nav-items";
import { useAuth } from "@/lib/auth";
import { useFeatureFlags } from "@/lib/use-feature";

/**
 * Os grupos de menu que esta pessoa, nesta organização, pode ver.
 *
 * `hidden: true` (Radiologia, Gestão) esconde incondicionalmente — MVP por enquanto.
 * `personalOnly`/`teamOnly` (Organizações/Equipe) são mutuamente exclusivos conforme `isPersonal`
 * da organização ativa. Grupos sem `organizationTypes` aparecem sempre. Organização SEM tipo
 * configurado (caso legado, de antes dessa etapa existir no onboarding) mostra tudo, permissivo;
 * só filtra de verdade quando a organização tiver tipo(s) configurado(s).
 *
 * Dentro de cada grupo que sobrevive, filtra item a item por `requiresPermission` (retrofit de UI
 * por permissão, issue #10) — grupo que fica sem nenhum item visível some inteiro (ex:
 * "Laboratório" pra quem não tem nenhuma das chaves).
 */
export function useGruposVisiveis() {
  const { user } = useAuth();
  const featureFlags = useFeatureFlags();

  return useMemo(() => {
    const activeTypes = user?.activeOrganization?.types ?? [];
    const isPersonal = user?.activeOrganization?.isPersonal ?? false;
    const permissions = user?.activeOrganization?.permissions;
    const role = user?.activeOrganization?.role;
    return navGroups
      .filter((group) => {
        if (group.hidden) return false;
        if (group.personalOnly && !isPersonal) return false;
        if (group.teamOnly && isPersonal) return false;
        if (!group.organizationTypes || group.organizationTypes.length === 0) return true;
        if (activeTypes.length === 0) return true;
        return group.organizationTypes.some((t) => activeTypes.includes(t));
      })
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          const exigidas = item.requiresPermission ? [item.requiresPermission].flat() : [];
          if (exigidas.length && permissions && !exigidas.some((k) => permissions[k])) return false;
          if (item.requiresFeature && !featureFlags[item.requiresFeature]) return false;
          if (item.requiresRole && role !== item.requiresRole) return false;
          if (item.ocultoEm?.some((t) => activeTypes.includes(t))) return false;
          return true;
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [
    user?.activeOrganization?.types,
    user?.activeOrganization?.isPersonal,
    user?.activeOrganization?.permissions,
    user?.activeOrganization?.role,
    featureFlags,
  ]);
}
